import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import { useDebounce } from '../../../hooks/useDebounce';
import { Respondent, PaginationData, SortConfig } from '../../../types';
import { getSortIcon, renderPagination } from '../../../utils/Utils';
import { getRelationshipString } from '../../../utils/Constants';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import './AdminRespondentList.css';

const AdminRespondentList: React.FC = () => {
  const [respondents, setRespondents] = useState<PaginationData>({} as PaginationData);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRespondents();
  }, [currentPage, sortConfig, debouncedSearchTerm, showDeleted]);


  const fetchRespondents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        sort: sortConfig.key,
        direction: sortConfig.direction,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(showDeleted && { show_deleted: 'true' })
      });

      const response = await axios.get(`/admin/respondents?${params}`);
      setRespondents(response.data);
    } catch (error) {
      console.error('Error fetching respondents:', error);
      setError('回答者一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleShowDeletedChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setShowDeleted(e.target.checked);
    setCurrentPage(1);
  };

  const handleDelete = async (respondentId: number, respondentName: string) => {
    if (!window.confirm(`${respondentName}さんの情報を削除してもよろしいですか？`)) {
      return;
    }

    try {
      await axios.delete(`/admin/respondents/${respondentId}`);
      alert(`${respondentName}さんの情報が削除されました。`);
      fetchRespondents();
    } catch (error: any) {
      alert(error.response?.data?.message || '削除に失敗しました。');
    }
  };

  const handleCsvDownload = async () => {
    try {
      const params = new URLSearchParams({
        sort: sortConfig.key,
        direction: sortConfig.direction,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(showDeleted && { show_deleted: 'true' })
      });
      
      const response = await axios.get(`/admin/respondents/export-csv?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `回答者一覧_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('CSV download error:', error);
      alert('CSVダウンロードに失敗しました。');
    }
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>

        <div className="navigation-container">
          <AdminNavigation respondents={!showDeleted ? respondents.total : undefined} />
          <div className="search-container">
            <div className="search-input-container">
              <Form>
              <Form.Control
                type="text"
                placeholder="回答者名／メールアドレス／候補者名"
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              </Form>
            </div>
          </div>
        </div>

        <div className="search-deleted-container">
          <Form.Check
            type="checkbox"
            id="show-deleted-respondents"
            label="削除されたデータのみ表示"
            checked={showDeleted}
            onChange={handleShowDeletedChange}
          />
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="candidates-table-container">
          <table className="candidates-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  回答者名 {getSortIcon('name', sortConfig)}
                </th>
                <th onClick={() => handleSort('candidate.name')} className="sortable">
                  候補者名 {getSortIcon('candidate.name', sortConfig)}
                </th>
                <th onClick={() => handleSort('client_name')} className="sortable">
                  クライアント名 {getSortIcon('client_name', sortConfig)}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  メールアドレス {getSortIcon('email', sortConfig)}
                </th>
                <th onClick={() => handleSort('relationship')} className="sortable">
                  関係性 {getSortIcon('relationship', sortConfig)}
                </th>
                <th onClick={() => handleSort('response_date')} className="sortable">
                  回答日 {getSortIcon('response_date', sortConfig)}
                </th>
                <th className="text-center">
                  <Button 
                    className="csv-download-button"
                    onClick={handleCsvDownload}
                    size="sm"
                  >
                    <FontAwesomeIcon icon={faFileCsv} className="me-1" />
                    CSVダウンロード
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <div className="blank-form-container">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">情報を読み込んでいます...</p>
                    </div>
                  </td>
                </tr>
              ) : (
              respondents.data && respondents.data.length > 0 ? (
                respondents.data.map((respondent: Respondent) => (
                  <tr key={respondent.id}>
                    <td>{respondent.name}</td>
                    <td>{respondent.candidate?.name || '-'}</td>
                    <td>{respondent.candidate?.user?.name || '-'}</td>
                    <td>{respondent.email}</td>
                    <td>{getRelationshipString(Number(respondent.relationship))}</td>
                    <td>{respondent.response?.created_at ? new Date(respondent.response.created_at).toLocaleDateString('ja-JP') : '-'}</td>
                    <td className="text-center">
                    {((respondent.deleted_at || '') === '') && (
                      <>
                        <Button 
                          className="detail-button me-1"
                          onClick={() => navigate(`/admin/respondents/${respondent.id}`)}
                        >
                          詳細
                        </Button>
                        <Button 
                          className="delete-button-icon"
                          onClick={() => handleDelete(respondent.id, respondent.name)}
                          title="削除"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </>
                    )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">該当するデータはありません。</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {respondents.last_page > 1 && renderPagination(currentPage, respondents.last_page, paginate)}
      </Container>
    </div>
  );
};

export default AdminRespondentList;
